import { Link } from 'react-router-dom'
import type { Supplier, SupplierMitigationTask } from '../../types'
import Modal from '../../components/ui/Modal'
import { resolveMitigationDisplayLocation } from '../../utils/regionToCoordinates'
import { EsBadge, EsButton } from './ui'
import { supplierDisplayName } from './supplierRollup'

function statusLabel(status: string): string {
  if (status === 'on_hold' || status === 'scheduled') return 'On hold'
  if (status === 'closed' || status === 'completed') return 'Closed'
  if (status === 'in_progress') return 'In progress'
  if (status === 'at_risk') return 'At risk'
  return 'Open'
}

interface MitigationDetailModalProps {
  task: SupplierMitigationTask | null
  supplier?: Pick<
    Supplier,
    'name' | 'display_name' | 'parent_name' | 'procurement_type' | 'location' | 'kenvue_region' | 'brand'
  > | null
  onClose: () => void
}

export function MitigationDetailModal({
  task,
  supplier = null,
  onClose,
}: MitigationDetailModalProps) {
  if (!task) return null

  const location = resolveMitigationDisplayLocation(task, supplier)
  const dateLabel = task.alert_date
    ? new Date(task.alert_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : task.created_at
      ? new Date(task.created_at).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null

  return (
    <Modal isOpen={!!task} onClose={onClose} className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-start gap-4 mb-4">
        <h2 className="text-xl font-bold text-es-navy pr-8">{task.task}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-es-muted hover:text-es-navy p-1 shrink-0"
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark text-xl" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <EsBadge tone="quiet" className="!text-sm !px-2 !py-1">
          {statusLabel(task.status)}
        </EsBadge>
        {task.priority && (
          <EsBadge tone="quiet" className="!text-sm !px-2 !py-1">
            {task.priority}
          </EsBadge>
        )}
      </div>

      {supplier && supplierDisplayName(supplier) && (
        <p className="text-sm text-es-body mt-3">
          <span className="text-es-muted">Supplier:</span> {supplierDisplayName(supplier)}
        </p>
      )}
      {location && (
        <p className="text-sm text-es-muted mt-1">
          <i className="fa-solid fa-location-dot mr-1" aria-hidden />
          {location}
        </p>
      )}
      {(task.brand || supplier?.brand) && (
        <p className="text-sm text-es-muted mt-1">Brand: {task.brand || supplier?.brand}</p>
      )}
      {dateLabel && <p className="text-sm text-es-muted mt-1">Alert date: {dateLabel}</p>}
      {task.category_manager && (
        <p className="text-sm text-es-muted mt-1">Category manager: {task.category_manager}</p>
      )}
      {task.regional_head && (
        <p className="text-sm text-es-muted mt-1">Regional head: {task.regional_head}</p>
      )}

      {task.comments && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-es-navy mb-1">Comments</h3>
          <p className="text-sm text-es-body whitespace-pre-wrap">{task.comments}</p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <Link to={`/early-signals/mitigation?task=${encodeURIComponent(task.id)}`} onClick={onClose}>
          <EsButton type="button" variant="primary">
            Open Mitigation tracker
          </EsButton>
        </Link>
        <EsButton type="button" variant="ghost" onClick={onClose}>
          Close
        </EsButton>
      </div>
    </Modal>
  )
}
